import {
  Component,
  ElementRef,
  OnInit,
  QueryList,
  ViewChildren,
} from '@angular/core';
import * as _ from 'lodash';
import { Subscription } from 'rxjs';
import { AnalyticsService } from 'src/app/components/logging/analytics.service';
import { FeaturesService } from 'src/app/components/plans/features.service';
import { UpgradeNoticeComponent } from 'src/app/components/plans/upgrade-notice/upgrade-notice.component';
import { RvSortableItem, RvSortableService } from 'src/app/rv-sortable';
import { StorageManagerService } from 'src/app/storage/services/storage-manager.service';
import { AttributeDataService } from '../../services/attribute-data.service';
import { BlueprintService } from '../../services/blueprint.service';
import { ComponentsService } from '../../services/components.service';
import { FileExistenceCheckService } from '../services/file-existence-check.service';
import { LogoImageService } from '../services/logo-image.service';
import { MediaSelectorService } from '../services/media-selector.service';
import { PlaylistComponentService } from '../services/playlist-component.service';
import {
  PlaylistService,
  PlaylistTemplateItem,
} from '../services/playlist.service';
@Component({
  selector: 'template-component-playlist',
  templateUrl: './playlist.component.html',
  standalone: false,
})
export class PlaylistComponent implements OnInit {
  selectedItem: any = {};
  subscription: Subscription;
  sortableService: RvSortableService<RvSortableItem>;

  @ViewChildren('ItemName') itemNameInputs: ElementRef[];
  @ViewChildren('UpgradeNotice') notices: QueryList<UpgradeNoticeComponent>;

  constructor(
    private elementRef: ElementRef,
    public playlistService: PlaylistService,
    private featuresService: FeaturesService,
    public componentsService: ComponentsService,
    private attributeDataService: AttributeDataService,
    private blueprintService: BlueprintService,
    public playlistComponentService: PlaylistComponentService,
    private analyticsFactory: AnalyticsService,
    private fileExistenceCheckService: FileExistenceCheckService,
    public storageManagerService: StorageManagerService,
  ) {
    this.playlistService.reset();

    componentsService.registerDirective({
      type: 'rise-playlist',
      element: this.elementRef.nativeElement,
      show: () => {
        this.playlistService.componentId = this.componentsService.selected.id;
        this.playlistService.playlistItems = [];

        playlistComponentService.onAddHandler = this.addItems.bind(this);

        this._load();

        setTimeout(() => {
          UpgradeNoticeComponent.hidePopovers(this.notices);
        });

        this.analyticsFactory.track('Playlist Viewed', {
          componentId: this.playlistService.componentId,
        });
      },
      onBackHandler: () => {
        playlistComponentService.onAddHandler = null;
      },
    });

    componentsService.registerDirective({
      type: 'rise-playlist-item',
      element: this.elementRef.nativeElement,
      show: () => {
        if (this.selectedItem.name) {
          this.componentsService.setPanelTitle(this.selectedItem.name);
        } else {
          const item =
            this.playlistService.playlistItems[this.selectedItem.key];

          this.componentsService.setPanelTitle(
            this.playlistService.getComponentName(item),
          );
        }
      },
      onBackHandler: () => {
        this.componentsService.resetPanelHeader();
      },
    });

    this.subscription = componentsService.componentValueUpdated$.subscribe(
      (source) => {
        this._setFileMissing(source, false);
      },
    );
  }

  ngOnInit() {
    this.sortableService = new RvSortableService<RvSortableItem>({
      items: this.ensurePlaylistItemsHaveIds(
        this.playlistService.playlistItems || [],
      ),
      getDisplayName: (item) => this.getDisplayName(item),
      onReorder: (items) => this.handleReorder(items),
      afterPositionChanged: () => this.playlistService.save(),
      hostElement: this.elementRef,
      appendTo: '.component-container',
    });
  }

  private ensurePlaylistItemsHaveIds(items: any[]): RvSortableItem[] {
    return items.map((item, index) => {
      if (typeof item === 'object' && item !== null) {
        if (!item.id) {
          item.id = `playlist-item-${Date.now()}-${index}`;
        }
        return item as RvSortableItem;
      }
      return item as RvSortableItem;
    });
  }

  private handleReorder(items: RvSortableItem[]): void {
    const currentOrder = this.playlistService.playlistItems.map(
      (item) => (item as RvSortableItem).rvSortableId,
    );

    for (let newIndex = 0; newIndex < items.length; newIndex++) {
      const itemId = items[newIndex].rvSortableId;
      const oldIndex = currentOrder.indexOf(itemId);

      if (oldIndex !== newIndex) {
        this.moveItem(oldIndex, newIndex);
        this.playlistService.save();
        break;
      }
    }
  }

  private _setFileMissing(source, value) {
    const file = this.playlistService.playlistItems.find(
      (item) => item.attributes?.source === source,
    );
    if (file) {
      file.fileMissing = value;
    }
  }

  _updatePlaylistComponents() {
    const allowedComponents = this.attributeDataService.getBlueprintData(
      this.playlistService.componentId,
      'allowed-components',
    );

    if (!allowedComponents || allowedComponents === '*') {
      this.playlistService.playlistComponents =
        ComponentsService.PLAYLIST_COMPONENTS;
    } else {
      var componentsArray = allowedComponents.split(',');

      this.playlistService.playlistComponents = _.filter(
        ComponentsService.PLAYLIST_COMPONENTS,
        (component: any) => {
          return componentsArray.indexOf(component.type) !== -1;
        },
      );
    }
  }

  _loadPresentationNames() {
    if (
      !this.playlistService.playlistItems ||
      !this.playlistService.playlistItems.length
    ) {
      return;
    }

    const presentationItems = this.playlistService.playlistItems.filter(
      this.isEmbeddedTemplate,
    );

    if (!presentationItems?.length) {
      return;
    }

    this.playlistComponentService.loadPresentationNames(presentationItems);
  }

  _load() {
    this._updatePlaylistComponents();

    const itemsJson = this.attributeDataService.getAvailableAttributeData(
      this.playlistService.componentId,
      'items',
    );
    const itemsWithIds = this.ensurePlaylistItemsHaveIds(
      this.playlistService.jsonToPlaylistItems(itemsJson),
    );
    this.playlistService.playlistItems = itemsWithIds as PlaylistTemplateItem[];

    if (this.sortableService) {
      this.sortableService.updateItems(itemsWithIds);
    }

    const fileNames = this.playlistService.playlistItems
      .filter(
        (item) =>
          item.attributes?.type ===
          MediaSelectorService.FOLDER_TYPE.STORAGE_FILE,
      )
      .map((file) => String(file.attributes.source));

    this.fileExistenceCheckService
      .requestMetadataFor(fileNames, LogoImageService.DEFAULT_IMAGE_THUMBNAIL)
      .then((metadata) => {
        metadata.forEach((meta) => {
          if (!meta.exists) {
            this._setFileMissing(meta.file, true);
          }
        });
      });

    this.playlistService.playlistItems
      .filter(
        (item) =>
          item.attributes?.type ===
          MediaSelectorService.FOLDER_TYPE.STORAGE_FOLDER,
      )
      .forEach((item) => {
        let source = String(item.attributes.source);
        source = source.substring(source.indexOf('/') + 1);
        // ensure the file type 'image,video,folder' is used in loadFiles function
        this.storageManagerService
          .loadFiles(source, StorageManagerService.FILE_TYPE.IMAGE_VIDEO_FOLDER)
          .then((files) => {
            item.fileMissing = files.length === 0;
          });
      });

    this._loadPresentationNames();
  }

  showComponentsDropdown() {
    return (
      this.blueprintService.isRiseInit() &&
      !!this.playlistService.playlistComponents &&
      this.playlistService.playlistComponents.length > 0
    );
  }

  showProperties() {
    this.componentsService.editComponent({
      type: 'rise-playlist-item',
    });
  }

  isEmbeddedTemplate(item) {
    return item.tagName === 'rise-embedded-template' || !item.tagName;
  }

  addItems(itemsToAdd, editItem?) {
    const itemToEdit =
      editItem && itemsToAdd[0] ? _.cloneDeep(itemsToAdd[0]) : null;
    const itemsWithIds = this.ensurePlaylistItemsHaveIds(itemsToAdd);
    this.playlistService.playlistItems =
      this.playlistService.playlistItems.concat(
        itemsWithIds as PlaylistTemplateItem[],
      );

    if (this.sortableService) {
      this.sortableService.updateItems(
        this.playlistService.playlistItems as RvSortableItem[],
      );
    }

    this.playlistService.save();

    console.debug('add items', itemsToAdd);

    if (itemToEdit) {
      this.playlistService.editComponent(itemToEdit);
    }
  }

  private _findItemNameInput(key: number): ElementRef {
    return this.itemNameInputs.find(
      (el) => el.nativeElement.id === `input-item-name-${key}`,
    );
  }

  getDisplayName(item: any): string {
    return this.isEmbeddedTemplate(item)
      ? item.label || item.name
      : this.playlistService.getComponentName(item);
  }

  getPlaylistItemIcon(item: PlaylistTemplateItem): string {
    return this.playlistService.getComponentIcon(item);
  }

  /**
   * Enter on the focused row opens the playlist item editor (same as clicking the label).
   */
  onPlaylistItemEnterKeyup(
    item: PlaylistTemplateItem,
    key: number,
    event: KeyboardEvent,
  ): void {
    const target = event.target as HTMLElement | null;
    if (
      !target ||
      target.closest(
        'input, textarea, [contenteditable="true"], button, a, rv-sortable-handle, template-attribute-components-menu-inline, template-attribute-components-menu-dropdown',
      )
    ) {
      return;
    }
    if (item?.editing || this.isEmbeddedTemplate(item)) {
      return;
    }
    if (this.requiresUpgrade(item)) {
      return;
    }
    this.editPlaylistItem(key);
  }

  /** Prevent duration control from trapping :focus-within on the row after mouse click. */
  onDurationPointerDown(event: PointerEvent): void {
    if (event.pointerType === 'mouse' && event.button === 0) {
      event.preventDefault();
    }
  }

  renameItem(key: number): void {
    this.playlistService.playlistItems[key].editing = true;
    const input = this._findItemNameInput(key);
    setTimeout(() => {
      input.nativeElement.focus();
    });
  }

  itemNameKeyup(key: number, event: KeyboardEvent): void {
    if (event.key === 'Escape') {
      const input = this._findItemNameInput(key).nativeElement;
      input.value = this.getDisplayName(
        this.playlistService.playlistItems[key],
      );
      input.blur();
    } else if (event.key === 'Enter') {
      this._findItemNameInput(key).nativeElement.blur();
    }
  }

  saveItemName(key: number, event: Event): void {
    const item = this.playlistService.playlistItems[key];
    const value = (<HTMLInputElement>event.target).value.trim();
    if (value !== this.getDisplayName(item)) {
      item.label = value;
      this.playlistService.save();
    }
    item.editing = false;
  }

  onDuplicateClick(key: number): void {
    this.playlistService.duplicatePlaylistItem(key, false);
  }

  onDeleteClick(key: number): void {
    this.playlistService.deletePlaylistItem(key);
  }

  sortItem(evt) {
    this.moveItem(evt.data.oldIndex, evt.data.newIndex);

    this.playlistService.save();
  }

  moveItem(oldIndex, newIndex) {
    this.playlistService.playlistItems.splice(
      newIndex,
      0,
      this.playlistService.playlistItems.splice(oldIndex, 1)[0],
    );
  }

  durationToText(item) {
    return item['play-until-done']
      ? 'PUD'
      : (item.duration ? item.duration : '10') + 's';
  }

  _updatePlayUntilDone(isSupported) {
    this.selectedItem['play-until-done-supported'] = isSupported;

    this.selectedItem['play-until-done'] =
      this.selectedItem['play-until-done-supported'] &&
      this.selectedItem['play-until-done'];
  }

  editProperties(key) {
    this.selectedItem = _.cloneDeep(this.playlistService.playlistItems[key]);
    this.selectedItem.key = key;

    //set default values
    this.selectedItem.duration = Number.isInteger(this.selectedItem.duration)
      ? this.selectedItem.duration
      : 10;
    this.selectedItem['transition-type'] = this.selectedItem['transition-type']
      ? this.selectedItem['transition-type']
      : 'normal';
    this.selectedItem.timeline = this.selectedItem.timeline
      ? this.selectedItem.timeline
      : {};

    if (!this.isEmbeddedTemplate(this.selectedItem)) {
      const layoutId = this.selectedItem.attributes?.layout?.layoutId;
      const component = this.playlistService.getComponentByTypeOrLayoutId(
        this.selectedItem.tagName,
        layoutId,
      );

      this._updatePlayUntilDone(!!component.playUntilDone);

      this.showProperties();
    } else {
      this.blueprintService
        .isPlayUntilDone(this.selectedItem.productCode)
        .then((res) => {
          this._updatePlayUntilDone(res);
        })
        .catch(() => {
          this._updatePlayUntilDone(false);
        })
        .finally(() => {
          this.showProperties();
        });
    }
  }

  saveTimeline() {
    this.saveProperties();

    this.analyticsFactory.track('Playlist Item Property Updated', {
      componentId: this.playlistService.componentId,
      propertyName: 'timeline',
    });
  }

  saveProperties() {
    const item = this.playlistService.playlistItems[this.selectedItem.key];

    item.duration = Number.isInteger(this.selectedItem.duration)
      ? this.selectedItem.duration
      : 10;
    item['play-until-done'] = this.selectedItem['play-until-done'];
    item['transition-type'] = this.selectedItem['transition-type'];
    item.timeline = this.selectedItem.timeline;

    this.playlistService.save();
  }

  savePlayUntilDone() {
    this.selectedItem['play-until-done'] =
      !this.selectedItem['play-until-done'];

    this.saveProperties();
  }

  editPlaylistItem(key) {
    const item = this.playlistService.playlistItems[key];

    if (this.requiresUpgrade(item)) {
      this.featuresService.showUpgradePlanModal(
        item.tagName === 'rise-powerbi' ? FeaturesService.FEATURE_POWER_BI : '',
      );
    } else {
      this.playlistService.editComponent(item, { isPlaylistItem: true });
    }
  }

  showAddPlaylistItem() {
    this.componentsService.editComponent({
      type: 'rise-playlist-item-selector',
    });
  }

  requiresUpgrade(item: PlaylistTemplateItem): boolean {
    if (item.tagName === 'rise-powerbi') {
      return !this.featuresService.isFeatureAvailable(
        FeaturesService.FEATURE_POWER_BI,
      );
    }
    if (item.tagName === 'rise-domo') {
      return !this.featuresService.isFeatureAvailable(
        FeaturesService.FEATURE_DOMO,
      );
    }
    return false;
  }
}
